import { useState } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { useQuery } from '@apollo/client'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { SheetManager } from 'react-native-actions-sheet'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { PAYMENT_METHODS } from '@/constants/common'
import { GET_USER, GET_USER_PAYMENT_METHODS } from '@/constants/graphql/queries'
import { cn } from '@/utils/cn'
import { groupPaymentMethods } from '@/utils/payment'
import { AuthContext } from '@/stores/auth'
import { Button, useModal, useToast } from '@/components/base'
import BottomActionPanel from '@/components/BottomActionPanel'
import { ChevronRight, Wallet } from '@/components/icon'
import { Stack } from '@/components/layout'
import LoadingScreen from '@/components/LoadingScreen'
import NavigationBar from '@/components/NavigationBar'
import SectionHeader from '@/components/SectionHeader'
import ServerErrorScreen from '@/components/ServerErrorScreen'
import SetTxPasswordModal from '@/components/SetTxPasswordModal'

export default function CardListScreen() {
  const [hasTxPassword, setHasTxPassword] = useState<boolean>(false)
  const router = useRouter()
  const { t } = useTranslation()
  const { bottom } = useSafeAreaInsets()
  const { openModal, closeModal } = useModal()
  const user = AuthContext.useSelector((state) => state.context.values?.user)
  const toast = useToast()
  const { data, loading, refetch } = useQuery(GET_USER_PAYMENT_METHODS, {
    onCompleted: () => {},
    onError: (error) => {
      console.log(error)
    }
  })

  useQuery(GET_USER, {
    variables: {
      id: user?.id
    },
    onCompleted: (data) => {
      setHasTxPassword(data.user.withdrawalPasswordEnabled)
    },
    onError: (error) => {
      console.error('error:', error)
    }
  })

  function handleAddPaymentMethod(paymentMethodsCount: number) {
    if (paymentMethodsCount >= 20) {
      toast.show({ variant: 'error', description: t('paymentMethod:maximumTwenty') })
      return
    }
    if (hasTxPassword) {
      SheetManager.show('add-payment-method')
    } else {
      openModal({
        children: <SetTxPasswordModal />,
        onClose: closeModal
      })
    }
  }

  if (loading) {
    return <LoadingScreen header={<NavigationBar hasBottomBorder={true}>{t('paymentMethod:title')}</NavigationBar>} />
  }

  if (!data) {
    return (
      <ServerErrorScreen
        onPress={refetch}
        header={<NavigationBar hasBottomBorder={true}>{t('paymentMethod:title')}</NavigationBar>}
      />
    )
  }

  const paymentMethods: Record<string, any[]> = groupPaymentMethods(data)
  const paymentMethodsCount = Object.values(paymentMethods).reduce((acc, cur) => acc + cur.length, 0)

  return (
    <>
      <Stack.Screen
        options={{
          header: () => <NavigationBar hasBottomBorder={true}>{t('paymentMethod:title')}</NavigationBar>
        }}
      />
      <View className={'flex-1 bg-background-secondary'}>
        {data.getUserPaymentMethods.length === 0 ? (
          <View className={'flex-1 items-center justify-center gap-4 p-4'} style={{ paddingBottom: bottom + 72 }}>
            <Wallet className={'size-20 text-text-quaternary'} />
            <Text className={'text-sm text-text-secondary'}>{t('paymentMethod:noPaymentMethods')}</Text>
          </View>
        ) : (
          <ScrollView className={'flex-1'} contentContainerStyle={{ paddingBottom: bottom + 72 }}>
            {PAYMENT_METHODS.map((method) => {
              if (paymentMethods[method].length === 0) {
                return null
              }
              return (
                <View key={method}>
                  <SectionHeader>{t(`payment:${method}`)}</SectionHeader>
                  {paymentMethods[method].map(
                    ({ info, id }: { info: Record<string, Record<string, string>>; id: string }, index: number) => (
                      <Pressable
                        key={id}
                        onPress={() => {
                          router.push(`/(main)/setting/payment-method/${id}`)
                        }}
                      >
                        {({ pressed }) => (
                          <View
                            className={cn(
                              'h-12 border-border-10 bg-background-primary',
                              pressed && 'bg-background-secondary',
                              index === 0 && 'border-t'
                            )}
                          >
                            <View
                              className={cn(
                                'h-full flex-row items-center justify-between border-b border-border-10 pr-4',
                                index === paymentMethods[method].length - 1 ? 'pl-4' : 'ml-4'
                              )}
                            >
                              <Text className={'text-base text-text-primary'}>
                                {method === 'bank' ? info[method].bankName : info[method].name}
                              </Text>
                              <View className={'flex-row items-center gap-2'}>
                                <Text
                                  className={'text-base text-text-primary'}
                                >{`*${info[method].account.slice(-4)}`}</Text>
                                <ChevronRight className={'size-6 text-text-tertiary'} />
                              </View>
                            </View>
                          </View>
                        )}
                      </Pressable>
                    )
                  )}
                </View>
              )
            })}
          </ScrollView>
        )}
        <BottomActionPanel>
          <Button className={'flex-1'} size={'md'} onPress={() => handleAddPaymentMethod(paymentMethodsCount)}>
            {t('paymentMethod:addPaymentMethod')}
          </Button>
        </BottomActionPanel>
      </View>
    </>
  )
}
